import React from 'react';
import { useTranslation } from 'react-i18next';
import ReactPlaceholder from 'react-placeholder';
import { useSelector } from 'react-redux';
import { Store } from 'store';
import getPublicAssetImageDesignName from 'store/publicAssetProduct/selector/getPublicAssetImageDesignName';
import getPublicAssetImageArtistName from 'store/publicAssetProduct/selector/getPublicAssetImageArtistName';
import { PublicAssetSources } from 'store/publicAssetProduct/types';
import OpText from 'components/Common/op-ui-wrappers/OpText/OpText';
import { Link } from 'react-router-dom';
import isDesktop from 'store/app/selector/isDesktop';
import ImageProviderInfo from 'store/publicAssetProduct/providers';
import styles from './StudioProductTitle.module.scss';

const StudioProductTitle = () => {
  const { t } = useTranslation();
  const artistName = useSelector(getPublicAssetImageArtistName);
  const designName = useSelector(getPublicAssetImageDesignName);
  const source = useSelector((state: Store) => state.publicAssetProduct.source) as PublicAssetSources;
  const isDesktopDevice = useSelector(isDesktop);

  const sourceTranslation = () => {
    return ImageProviderInfo[source]?.translationKey || '';
  };

  const imageSourceName = sourceTranslation();
  const linkToArtistSearch = `/search?query=${artistName}`;
  const linkToSourceSearch = `/search?query=${source}`;

  if (!source && !artistName) {
    return null;
  }

  return (
    <div className={styles.productTitle} itemScope itemType="http://schema.org/Product">
      <ReactPlaceholder
        showLoadingAnimation
        type="textRow"
        ready={!!designName}
        style={{ width: '100%', backgroundColor: '#e8e8e8', height: '68px', marginBottom: '4px' }}
      >
        <h1 itemProp="name" className={styles.headline}>
          {designName}
        </h1>
      </ReactPlaceholder>
      <ReactPlaceholder
        showLoadingAnimation
        type="textRow"
        ready={!!artistName || !!source}
        style={{ width: '100%', backgroundColor: '#e8e8e8', height: '22px', marginBottom: '16px' }}
      >
        <div className={styles.designSource}>
          <OpText
            variant={isDesktopDevice ? 'body-large-regular' : 'body-medium-regular'}
            className={styles.designedBy}
          >
            {source === PublicAssetSources.Shutterstock ? `${t('txt_from')}` : t('txt_by')}
          </OpText>
          {artistName && (
            <Link to={linkToArtistSearch}>
              <OpText
                itemProp="author"
                className={styles.artistName}
                variant={isDesktopDevice ? 'body-large-bold' : 'body-medium-bold'}
              >
                {artistName}
              </OpText>
            </Link>
          )}
          {source === PublicAssetSources.Shutterstock && (
            <Link to={linkToSourceSearch}>
              <OpText
                className={styles.sourceName}
                style={{ textTransform: 'capitalize' }}
                variant={isDesktopDevice ? 'body-large-bold' : 'body-medium-bold'}
              >
                {source}
              </OpText>
            </Link>
          )}

          {imageSourceName && (
            <>
              <OpText variant={isDesktopDevice ? 'body-large-bold' : 'body-medium-bold'}> | </OpText>
              <Link to={linkToSourceSearch}>
                <OpText
                  className={styles.sourceName}
                  variant={isDesktopDevice ? 'body-large-bold' : 'body-medium-bold'}
                >
                  {t(imageSourceName)}
                </OpText>
              </Link>
            </>
          )}
        </div>
      </ReactPlaceholder>
    </div>
  );
};

export default StudioProductTitle;
